import _ from 'lodash'
import PropTypes from 'prop-types'
import * as React from 'react'

import {autobind} from '@base/decorators'

import dropdownCss from './dropdown.css'
import fadeCss from './fade.css'
import fadeDelayedCss from './fadeDelayed.css'
import lazyPopCss from './lazyPop.css'
import popCss from './pop.css'

export const animations = {
  fade: fadeCss,
  fadeDelayed: fadeDelayedCss,
  pop: popCss,
  dropdown: dropdownCss,
  lazyPop: lazyPopCss,
}

export const animationPropType = PropTypes.oneOfType([
  PropTypes.oneOf(Object.keys(animations)),
  PropTypes.object,
])

export type AnimationObject = {
  appear?: string
  appearActive?: string
  enter?: string
  enterActive?: string
  exit?: string
  exitActive?: string
  appearTimeout?: number | string
  enterTimeout?: number | string
  exitTimeout?: number | string
}

export type ResolvedAnimation = {
  appear?: string
  appearActive?: string
  enter?: string
  enterActive?: string
  exit?: string
  exitActive?: string
  appearTimeout: number
  enterTimeout: number
  exitTimeout: number
}

export type AnimationType =
  | 'fade'
  | 'fadeDelayed'
  | 'pop'
  | 'dropdown'
  | 'lazyPop'
  | AnimationObject

function resolveAnimationTimeout(
  timeout: string | number | undefined
): number | undefined {
  return typeof timeout === 'string' ? parseInt(timeout, 10) : timeout
}

export function resolveAnimation(
  animationOrName: AnimationType
): ResolvedAnimation {
  let animation
  if (typeof animationOrName === 'string') {
    animation = animations[animationOrName] || {}
  } else {
    animation = animationOrName || {}
  }
  return {
    // previous versions of react-animation-group accepted leave instead of exit, so let's normalize them here in case they're used
    exit: animation.leave,
    exitActive: animation.leaveActive,
    ...animation,
    appearTimeout: resolveAnimationTimeout(animation.appearTimeout) ?? 0,
    enterTimeout: resolveAnimationTimeout(animation.enterTimeout) ?? 0,
    exitTimeout:
      resolveAnimationTimeout(
        animation.exitTimeout ?? animation.leaveTimeout
      ) ?? 0,
  }
}

type CSSAnimationProps = {
  children: React.ReactElement<any>
  animation: AnimationType
  type: 'appear' | 'enter' | 'exit'
  onAnimationBegin?: (() => void) | null
  onAnimationEnd?: (() => void) | null
  style?: any
}

type State = {
  phase: boolean | string
}

export default class CSSAnimation extends React.Component<
  CSSAnimationProps,
  State
> {
  static propTypes = {
    children: PropTypes.element.isRequired,
    animation: animationPropType,
    type: PropTypes.string,
    onAnimationBegin: PropTypes.func,
    onAnimationEnd: PropTypes.func,
  }

  state = {
    phase: false,
  }

  _beginTimeout: any | undefined | null
  _endTimeout: any | undefined | null

  componentDidMount() {
    this.animationWillBegin()
  }

  componentDidUpdate(prevProps: CSSAnimationProps) {
    if (
      prevProps.animation !== this.props.animation ||
      prevProps.type !== this.props.type
    ) {
      this.animationWillBegin()
    }
  }

  componentWillUnmount() {
    this.setEndTimeout(0)
    if (this._beginTimeout != null) cancelAnimationFrame(this._beginTimeout)
    if (this._endTimeout != null) clearTimeout(this._endTimeout)
    this._beginTimeout = null
    this._endTimeout = null
  }

  animationWillBegin() {
    const timeout = this.animationTimeout
    if (timeout) {
      this.setBeginTimeout(true)
      this.setEndTimeout(timeout)
      this.setState({phase: 'prepare'})
    } else {
      if (this.hasAnimation) {
        // eslint-disable-next-line no-console
        console.warn(
          `Animation ${String(this.props.animation)} doesn't have timeout for ${
            this.props.type
          } animation type` +
            (TESTING
              ? '\nPlease note, that in tests the CSS files are not resolved and CSS-based animation will produce this warning'
              : '')
        )
      }
      this.setBeginTimeout(false)
      this.setEndTimeout(0)
      this.setState({phase: false})
    }
  }

  @autobind
  animationDidBegin() {
    this.setState({phase: 'active'})
    if (this.props.onAnimationBegin) this.props.onAnimationBegin()
  }

  @autobind
  animationDidEnd() {
    this.setState({phase: false})
    if (this.props.onAnimationEnd) this.props.onAnimationEnd()
  }

  setBeginTimeout(enable: boolean) {
    if (this._beginTimeout != null) cancelAnimationFrame(this._beginTimeout)
    if (enable) {
      this._beginTimeout = requestAnimationFrame(this.animationDidBegin)
    } else {
      this._beginTimeout = null
    }
  }

  setEndTimeout(timeout: number) {
    if (this._endTimeout != null) clearTimeout(this._endTimeout)
    if (timeout) {
      this._endTimeout = setTimeout(this.animationDidEnd, timeout)
    } else {
      this._endTimeout = null
    }
  }

  get hasAnimation(): boolean {
    return !!(this.props.animation && this.props.type)
  }

  get animation(): AnimationObject {
    return resolveAnimation(this.props.animation)
  }

  get animationTimeout(): number {
    if (!this.props.type) return 0
    const animation = this.animation
    // @ts-ignore Ignored when bumping to TS 4.4.3
    return animation && (animation[`${this.props.type}Timeout`] || 0)
  }

  getClassName() {
    if (!this.hasAnimation) return ''
    const animation = this.animation
    const type = this.props.type
    // @ts-ignore Ignored during migration
    if (this.state.phase === 'prepare') return `${animation[type] || ''}`
    return `${animation[type] || ''} ${animation[type + 'Active'] || ''}`
  }

  render() {
    const child = this.props.children
    if (!child) return null
    const props = _.omit(this.props, [
      'className',
      'style',
      'animation',
      'type',
      'onAnimationBegin',
      'onAnimationEnd',
      'children',
    ])
    return React.cloneElement(child, {
      ...props,
      className: (child.props.className || '') + ' ' + this.getClassName(),
      style: {...child.props.style, ...this.props.style},
    })
  }
}
